import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { useAuth } from "../../../AuthContext";
import {
  FaClipboardList,
  FaTrash,
  FaPlus,
  FaFileExcel,
  FaChevronDown,
  FaChevronUp,
  FaUserTie,
  FaSortAmountUp,
} from "react-icons/fa";

const API_BASE_URL = "https://sapl.cantarellisolucoes.com.br/wp-json/custom/v1";

const PautasSessoes = () => {
  const { user } = useAuth();
  const toast = useRef(null);

  // Estados
  const [loading, setLoading] = useState(false);
  const [pautas, setPautas] = useState([]);
  const [sessoes, setSessoes] = useState([]);
  const [sessaoSelecionada, setSessaoSelecionada] = useState(null);
  const [materias, setMaterias] = useState([]);
  const [parlamentares, setParlamentares] = useState([]);
  const [tiposTramitacao, setTiposTramitacao] = useState([]);
  const [pareceres, setPareceres] = useState([]);

  // Estados do modal
  const [modalVisible, setModalVisible] = useState(false);
  const [formData, setFormData] = useState({
    sessao_id: null,
    materia_id: null,
    ordem: 0,
    tipo_tramitacao: "unica",
    relator_id: null,
    parecer: "",
    status: "pendente",
    observacoes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Função genérica para requisições
  const apiRequest = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${user?.token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
        ...options.headers,
      },
      credentials: "include",
    });

    const data = await response.json();

    if (!response.ok) {
      throw { status: response.status, data };
    }

    return data;
  };

  // Carregar sessões para dropdown
  const carregarSessoes = useCallback(async () => {
    try {
      const response = await apiRequest("/sessoes");
      setSessoes(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar sessões:", error);
    }
  }, [user?.token]);

  // Carregar matérias disponíveis
  const carregarMaterias = useCallback(async () => {
    try {
      const response = await apiRequest("/materias?status=em_tramitacao");
      setMaterias(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar matérias:", error);
    }
  }, [user?.token]);

  // Carregar parlamentares para relator
  const carregarParlamentares = useCallback(async () => {
    try {
      const response = await apiRequest("/parlamentares?ativo=true");
      setParlamentares(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar parlamentares:", error);
    }
  }, [user?.token]);

  // Carregar configurações
  const carregarConfiguracoes = useCallback(async () => {
    try {
      const [tiposRes, pareceresRes] = await Promise.all([
        apiRequest("/tipos-tramitacao"),
        apiRequest("/pareceres"),
      ]);
      setTiposTramitacao(tiposRes.data || []);
      setPareceres(pareceresRes.data || []);
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
    }
  }, [user?.token]);

  // Carregar pautas da sessão selecionada
  const carregarPautas = useCallback(async () => {
    if (!sessaoSelecionada) {
      setPautas([]);
      return;
    }

    setLoading(true);
    try {
      // USAR A ROTA CORRETA DO SEU BACKEND
      const response = await apiRequest(
        `/sessoes/${sessaoSelecionada.id}/pautas`,
      );
      setPautas(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar pautas:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Não foi possível carregar a pauta",
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [user?.token, sessaoSelecionada]);

  // Efeitos
  useEffect(() => {
    if (user?.token) {
      carregarConfiguracoes();
      carregarSessoes();
      carregarMaterias();
      carregarParlamentares();
    }
  }, [
    user?.token,
    carregarConfiguracoes,
    carregarSessoes,
    carregarMaterias,
    carregarParlamentares,
  ]);

  useEffect(() => {
    carregarPautas();
  }, [sessaoSelecionada, carregarPautas]);

  // Abrir modal para adicionar item
  // No useEffect que carrega o modal ou no abrirModalAdicionar
  const abrirModalAdicionar = () => {
    const nextOrdem = pautas.length + 1;
    setFormData({
      sessao_id: sessaoSelecionada?.id || null, // <-- IMPORTANTE: preencher com o ID da sessão selecionada
      materia_id: null,
      ordem: nextOrdem,
      tipo_tramitacao: "unica",
      relator_id: null,
      parecer: "",
      status: "pendente",
      observacoes: "",
    });
    setModalVisible(true);
  };

  // Salvar item na pauta
  const salvarPauta = async () => {
    console.log("=== DEBUG SALVAR PAUTA ===");
    console.log("formData.sessao_id:", formData.sessao_id);
    console.log("sessaoSelecionada:", sessaoSelecionada);
    console.log("formData.materia_id:", formData.materia_id);
    console.log("formData.ordem:", formData.ordem);

    if (!formData.sessao_id) {
      console.log("Erro: sessao_id não encontrado");
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "Selecione uma sessão",
        life: 3000,
      });
      return;
    }

    if (!formData.materia_id) {
      console.log("Erro: materia_id não encontrado");
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "Selecione uma matéria",
        life: 3000,
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        materia_id: formData.materia_id,
        ordem: parseInt(formData.ordem),
        tipo_tramitacao:
          formData.tipo_tramitacao?.valor || formData.tipo_tramitacao,
        relator_id: formData.relator_id || null,
        parecer: formData.parecer?.valor || formData.parecer,
        status: "pendente",
        observacoes: formData.observacoes,
      };

      console.log("Payload enviado:", JSON.stringify(payload, null, 2));
      console.log("URL:", `/sessoes/${formData.sessao_id}/pautas`);

      const response = await apiRequest(
        `/sessoes/${formData.sessao_id}/pautas`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );

      console.log("Resposta:", response);

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Item adicionado à pauta",
        life: 3000,
      });

      setModalVisible(false);
      carregarPautas();
    } catch (error) {
      console.error("Erro completo:", error);
      console.error("Resposta do erro:", error.data);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: error.data?.message || "Falha ao adicionar item",
        life: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Remover item da pauta
  const confirmarRemocao = (pauta) => {
  confirmDialog({
    header: "",
    style: { width: "380px" },
    className: "custom-confirm-dialog",

    message: (
      <div className="flex flex-column align-items-center text-center">
        <i
          className="pi pi-exclamation-triangle"
          style={{ fontSize: "3rem", color: "#f59e0b", marginBottom: "1rem" }}
        ></i>

        <span
          style={{
            fontSize: "1.1rem",
            fontWeight: "500",
            marginBottom: "0.5rem",
          }}
        >
          Confirmar remoção
        </span>

        <span style={{ fontSize: "0.95rem", color: "#6b7280" }}>
          Deseja realmente <strong>remover</strong> o item da pauta
        </span>

        <span
          style={{ fontSize: "1rem", fontWeight: "600", marginTop: "0.5rem" }}
        >
          {pauta.materia_tipo} nº {pauta.materia_numero}/{pauta.materia_ano}?
        </span>
      </div>
    ),

    footer: (options) => (
      <div className="flex justify-content-center gap-2 w-full">
        <button
          className="p-button p-component p-button-sm p-button-secondary"
          onClick={options.reject}
        >
          <span className="p-button-label">Cancelar</span>
        </button>

        <button
          className="p-button p-component p-button-sm p-button-danger"
          onClick={options.accept}
        >
          <span className="p-button-label">Remover</span>
        </button>
      </div>
    ),

    accept: async () => {
      try {
        await apiRequest(`/pautas/${pauta.id}`, { method: "DELETE" });
        toast.current?.show({
          severity: "success",
          summary: "Removido",
          detail: `Item ${pauta.materia_tipo} nº ${pauta.materia_numero}/${pauta.materia_ano} removido da pauta`,
          life: 3000,
        });
        carregarPautas();
      } catch (error) {
        console.error("Erro ao remover item:", error);
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: error.data?.message || "Falha ao remover item da pauta",
          life: 5000,
        });
      }
    },
  });
};

  // Templates da tabela
  const ordemBody = (rowData) => (
    <span style={{ fontSize: "0.9rem", fontWeight: "600" }}>
      {rowData.ordem}º
    </span>
  );

  const materiaBody = (rowData) => (
    <div>
      <div style={{ fontSize: "0.9rem", fontWeight: "500" }}>
        {rowData.materia_tipo} nº {rowData.materia_numero}/{rowData.materia_ano}
      </div>
      <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>
        {rowData.ementa?.substring(0, 60)}...
      </div>
    </div>
  );

  const tramitacaoBody = (rowData) => {
    const tipo = tiposTramitacao.find(
      (t) => t.valor === rowData.tipo_tramitacao,
    );
    return (
      <Tag value={tipo?.label || rowData.tipo_tramitacao} severity="info" />
    );
  };

  const relatorBody = (rowData) => (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <div
        style={{
          width: "28px",
          height: "28px",
          borderRadius: "50%",
          backgroundColor: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#3b82f6",
        }}
      >
        <FaUserTie size={12} />
      </div>
      <span style={{ fontSize: "0.85rem" }}>
        {rowData.relator_nome || "Não definido"}
      </span>
    </div>
  );

  const parecerBody = (rowData) => {
    const parecer = pareceres.find((p) => p.valor === rowData.parecer);
    if (!rowData.parecer)
      return <span style={{ fontSize: "0.85rem", color: "#9ca3af" }}>---</span>;
    return (
      <Tag
        value={parecer?.label || rowData.parecer}
        severity={rowData.parecer === "favoravel" ? "success" : "danger"}
      />
    );
  };

  const acoesBody = (rowData) => (
    <Button
      icon="pi pi-trash"
      size="small"
      severity="warning"
      tooltip="Remover da pauta"
      className="custom-button button-efeito"
      style={{ width: "32px", height: "32px" }}
      onClick={() => confirmarRemocao(rowData)}
    />
  );

  const emptyMessage = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px",
        color: "#6b7280",
      }}
    >
      <FaClipboardList
        size={48}
        style={{ color: "#9ca3af", marginBottom: "20px" }}
      />
      <h3 style={{ marginBottom: "8px", color: "#374151", fontWeight: "500" }}>
        Nenhum item na pauta
      </h3>
      <p
        style={{
          textAlign: "center",
          maxWidth: "400px",
          fontSize: "0.85rem",
          lineHeight: "1.5",
        }}
      >
        A pauta está vazia para esta sessão.
        <br />
        Clique em "Adicionar" para incluir matérias na ordem do dia.
      </p>
    </div>
  );

  const modalFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancelar"
        severity="secondary"
        icon="pi pi-times"
        onClick={() => setModalVisible(false)}
        outlined
      />
      <Button
        label="Adicionar"
        severity="success"
        icon="pi pi-check"
        onClick={salvarPauta}
        loading={submitting}
        autoFocus
      />
    </div>
  );

  return (
    <div className="dashboard-container">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="dashboard-header">
        <h1>Pautas de Sessões</h1>
        <p>Gestão da ordem do dia das sessões plenárias</p>
      </div>

      <div className="page-content">
        {/* Seção de escolha da sessão */}
        <Card style={{ padding: "0", marginBottom: "10px" }}>
          <div style={{ padding: "0 10px" }}>
            <h2 style={{ margin: 0, color: "#374151" }}>Selecione a Sessão</h2>
          </div>

          <div style={{ padding: "0 10px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "3fr 1fr",
                gap: "24px",
                alignItems: "end",
              }}
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <label
                  style={{
                    fontWeight: "500",
                    color: "#374151",
                    fontSize: "0.95rem",
                  }}
                >
                  Escolha uma Sessão
                </label>
                <Dropdown
                  value={sessaoSelecionada}
                  options={sessoes}
                  onChange={(e) => setSessaoSelecionada(e.value)}
                  optionLabel="numero"
                  itemTemplate={(option) => (
                    <div>
                      Sessão nº {option.numero} -{" "}
                      {new Date(option.data).toLocaleDateString("pt-BR")}
                    </div>
                  )}
                  valueTemplate={(option) =>
                    option
                      ? `Sessão nº ${option.numero}`
                      : "Selecione uma sessão"
                  }
                  placeholder="Selecione uma sessão"
                  showClear
                />
              </div>

              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <Button
                  label="Adicionar Item"
                  icon="pi pi-plus"
                  severity="success"
                  onClick={abrirModalAdicionar}
                  disabled={!sessaoSelecionada}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Tabela de pauta */}
        <Card style={{ padding: "0" }}>
          <div style={{ padding: "0 0 10px 0", width: "100%" }}>
            <Message
              severity="info"
              text={
                sessaoSelecionada
                  ? `Sessão nº ${sessaoSelecionada.numero} • Total de itens: ${pautas.length}`
                  : "Selecione uma sessão para visualizar a pauta"
              }
              style={{ width: "100%" }}
            />
          </div>

          {loading ? (
            <div className="loading-container">
              <ProgressSpinner />
            </div>
          ) : (
            <DataTable
              value={pautas}
              dataKey="id"
              style={{ width: "100%" }}
              paginator={pautas.length > 10}
              rows={10}
              emptyMessage={emptyMessage}
              showGridlines
              stripedRows
            >
              <Column
                header="Ordem"
                body={ordemBody}
                sortable
                style={{ width: "60px" }}
              />
              <Column
                header="Matéria"
                body={materiaBody}
                sortable
                style={{ minWidth: "350px" }}
              />
              <Column
                header="Tp Tramitação"
                body={tramitacaoBody}
                sortable
                style={{ width: "180px" }}
              />
              <Column
                header="Relator"
                body={relatorBody}
                sortable
                style={{ width: "300px" }}
              />
              <Column
                header="Parecer"
                body={parecerBody}
                sortable
                style={{ width: "120px" }}
              />
              <Column
                header="Ações"
                body={acoesBody}
                style={{ width: "80px" }}
              />
            </DataTable>
          )}
        </Card>
      </div>

      {/* Modal de Adicionar Item */}
      <Dialog
        visible={modalVisible}
        style={{ width: "700px", maxWidth: "90vw" }}
        modal
        className="p-fluid modern-dialog"
        onHide={() => setModalVisible(false)}
        footer={modalFooter}
        header={
          <div className="flex align-items-center gap-2">
            <div
              className="bg-primary bg-opacity-10 border-circle p-2"
              style={{ borderRadius: "12px" }}
            >
              <i className="pi pi-plus-circle text-primary text-xl"></i>
            </div>
            <span className="font-bold text-xl">Adicionar Item à Pauta</span>
          </div>
        }
      >
        <div
          className="grid p-fluid"
          style={{
            backgroundColor: "#fafafa",
            border: "1px solid #e8e8e8",
            padding: "15px",
            borderRadius: "12px",
            margin: 0,
          }}
        >
          <div className="col-12">
            <div className="field" style={{ marginBottom: "15px" }}>
              <label className="font-semibold mb-1">Sessão *</label>
              <div
                style={{
                  padding: "0.75rem",
                  backgroundColor: "#f3f4f6",
                  borderRadius: "6px",
                  border: "1px solid #e5e7eb",
                  color: "#374151",
                  fontWeight: "500",
                }}
              >
                Sessão nº {sessaoSelecionada?.numero} -{" "}
                {sessaoSelecionada
                  ? new Date(sessaoSelecionada.data).toLocaleDateString("pt-BR")
                  : "Nenhuma sessão selecionada"}
              </div>
              <input type="hidden" value={formData.sessao_id} />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="field" style={{ marginBottom: "15px" }}>
              <label className="font-semibold mb-1">Ordem *</label>
              <InputText
                value={formData.ordem}
                onChange={(e) =>
                  setFormData({ ...formData, ordem: e.target.value })
                }
                placeholder="Ordem na pauta"
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="field" style={{ marginBottom: "15px" }}>
              <label className="font-semibold mb-1">Matéria *</label>
              <Dropdown
                value={formData.materia_id}
                options={materias}
                onChange={(e) =>
                  setFormData({ ...formData, materia_id: e.value })
                }
                optionLabel="ementa"
                optionValue="id" // <-- ADICIONE ESTA LINHA
                itemTemplate={(option) => (
                  <div>
                    {option.tipo} nº {option.numero}/{option.ano} -{" "}
                    {option.ementa?.substring(0, 60)}...
                  </div>
                )}
                valueTemplate={(option) =>
                  option
                    ? `${option.tipo} nº ${option.numero}/${option.ano}`
                    : "Selecione uma matéria"
                }
                placeholder="Selecione uma matéria"
                filter
                filterBy="ementa,tipo,numero"
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="field" style={{ marginBottom: "15px" }}>
              <label className="font-semibold mb-1">Tipo de Tramitação</label>
              <Dropdown
                value={formData.tipo_tramitacao}
                options={tiposTramitacao}
                onChange={(e) =>
                  setFormData({ ...formData, tipo_tramitacao: e.value })
                }
                optionLabel="label"
                placeholder="Selecione"
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="field" style={{ marginBottom: "15px" }}>
              <label className="font-semibold mb-1">Relator</label>
              <Dropdown
                value={formData.relator_id}
                options={parlamentares.map((p) => ({
                  label: p.nome,
                  value: p.id,
                }))}
                onChange={(e) =>
                  setFormData({ ...formData, relator_id: e.value })
                }
                optionLabel="label"
                optionValue="value" // <-- ADICIONE
                placeholder="Selecione o relator"
                filter
                showClear
              />
            </div>
          </div>

          <div className="col-12">
            <div className="field" style={{ marginBottom: "15px" }}>
              <label className="font-semibold mb-1">Parecer</label>
              <Dropdown
                value={formData.parecer}
                options={pareceres}
                onChange={(e) => setFormData({ ...formData, parecer: e.value })}
                optionLabel="label"
                optionValue="valor" // <-- ADICIONE
                placeholder="Selecione o parecer"
                showClear
              />
            </div>
          </div>

          <div className="col-12">
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="font-semibold mb-1">Observações</label>
              <InputTextarea
                rows={3}
                value={formData.observacoes}
                onChange={(e) =>
                  setFormData({ ...formData, observacoes: e.target.value })
                }
                placeholder="Informações adicionais sobre este item..."
              />
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default PautasSessoes;
